import { useMemo, useRef, useState } from "react";
import { useApp } from "@/App";

const SLOT_X = [-128, 0, 128];
const IDLE_LAYOUT = {
  0: { x: 0, y: -104 },
  1: { x: -128, y: 26 },
  2: { x: 128, y: 26 },
};

const PAYOUT_MULTIPLIER = 2.85;
const MIN_BET = 0.1;
const MAX_BET = 10;
const BET_STEP = 0.1;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const formatBet = (value) => Number(value).toFixed(2).replace(/\.?0+$/, "");

const bySlot = (mapping, slot) => {
  const found = Object.entries(mapping).find(([, s]) => s === slot);
  return found ? Number(found[0]) : 0;
};

const randomPair = (prevKey) => {
  const pairs = [
    [0, 1],
    [1, 2],
    [0, 2],
  ];
  const available = prevKey ? pairs.filter(([a, b]) => `${a}-${b}` !== prevKey) : pairs;
  return available[Math.floor(Math.random() * available.length)];
};

const initialCups = () => [
  { id: 0, x: IDLE_LAYOUT[0].x, y: IDLE_LAYOUT[0].y, rot: 0 },
  { id: 1, x: IDLE_LAYOUT[1].x, y: IDLE_LAYOUT[1].y, rot: 0 },
  { id: 2, x: IDLE_LAYOUT[2].x, y: IDLE_LAYOUT[2].y, rot: 0 },
];

const getErrorMessage = (error) => {
  const detail = String(error?.response?.data?.detail || "");
  if (detail === "payment_in_progress") return "Сейчас недоступно: идет другая операция";
  if (detail === "invalid_bet") return "Ставка должна быть от 0.1 до 10 USDT";
  if (detail.toLowerCase().includes("insufficient")) return "Недостаточно баланса";
  return detail || "Ошибка раунда. Попробуйте еще раз";
};

const MagicCupPage = () => {
  const { api, haptic, refreshData, balances } = useApp();
  const actionLockRef = useRef(false);

  const [phase, setPhase] = useState("idle"); // idle | mixing | choose | reveal
  const [cups, setCups] = useState(initialCups);
  const [cupToSlot, setCupToSlot] = useState({ 0: 1, 1: 0, 2: 2 });
  const [ballCupId, setBallCupId] = useState(0);
  const [revealedCupIds, setRevealedCupIds] = useState([]);
  const [bet, setBet] = useState(3);
  const [betInput, setBetInput] = useState("3");
  const [transitionMs, setTransitionMs] = useState(200);
  const [transitionEase, setTransitionEase] = useState("cubic-bezier(0.22, 0.61, 0.36, 1)");
  const [statusText, setStatusText] = useState("");
  const [lastOutcome, setLastOutcome] = useState(null);

  const usdtAvailable = useMemo(() => {
    const row = (balances || []).find((item) => String(item?.currency || "").toUpperCase() === "USDT");
    const amount = Number(row?.amount ?? 0);
    const hold = Number(row?.hold ?? 0);
    const available = Number(row?.available ?? (amount - hold));
    return Number.isFinite(available) ? Math.max(0, available) : 0;
  }, [balances]);

  const currentWinningSlot = cupToSlot[ballCupId];
  const controlsLocked = phase !== "idle";

  const ballStyle = useMemo(() => {
    if (phase === "idle" && revealedCupIds.length === 0) {
      return {
        transform: "translate(-50%, -50%) translate(0px, 22px)",
        opacity: 1,
      };
    }

    const ballVisible = phase === "idle" ? true : revealedCupIds.includes(ballCupId);
    return {
      transform: `translate(-50%, -50%) translate(${SLOT_X[currentWinningSlot]}px, 32px)`,
      opacity: ballVisible ? 1 : 0,
    };
  }, [ballCupId, currentWinningSlot, phase, revealedCupIds]);

  const applyCupPatch = async (patches, duration, easing = "cubic-bezier(0.22, 0.61, 0.36, 1)") => {
    setTransitionMs(duration);
    setTransitionEase(easing);
    setCups((prev) =>
      prev.map((cup) => {
        const patch = patches[cup.id];
        return patch ? { ...cup, ...patch } : cup;
      })
    );
    await sleep(duration + 16);
  };

  const resetToIdle = async () => {
    await applyCupPatch(
      {
        0: { x: IDLE_LAYOUT[0].x, y: IDLE_LAYOUT[0].y, rot: 0 },
        1: { x: IDLE_LAYOUT[1].x, y: IDLE_LAYOUT[1].y, rot: 0 },
        2: { x: IDLE_LAYOUT[2].x, y: IDLE_LAYOUT[2].y, rot: 0 },
      },
      220
    );
    setCupToSlot({ 0: 1, 1: 0, 2: 2 });
    setRevealedCupIds([]);
  };

  const clampBet = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return MIN_BET;
    return Math.max(MIN_BET, Math.min(MAX_BET, Number(num.toFixed(2))));
  };

  const setSafeBet = (nextBet) => {
    const clamped = clampBet(nextBet);
    setBet(clamped);
    setBetInput(formatBet(clamped));
  };

  const startRound = async () => {
    if (actionLockRef.current) return;
    if (usdtAvailable + 1e-9 < bet) {
      haptic("light");
      setStatusText(`Недостаточно USDT. Доступно: ${formatBet(usdtAvailable)} USDT`);
      setLastOutcome(null);
      return;
    }

    actionLockRef.current = true;
    haptic("medium");
    setLastOutcome(null);
    setStatusText("Перемешиваем...");
    setPhase("mixing");
    setRevealedCupIds([]);

    await resetToIdle();

    await applyCupPatch(
      {
        0: { x: SLOT_X[1], y: 0, rot: 0 },
        1: { x: SLOT_X[0], y: 0, rot: 0 },
        2: { x: SLOT_X[2], y: 0, rot: 0 },
      },
      220
    );

    let mapping = { 0: 1, 1: 0, 2: 2 };
    let prevPair = "";
    const shuffleSteps = 20 + Math.floor(Math.random() * 7); // 20..26

    for (let i = 0; i < shuffleSteps; i += 1) {
      const [slotA, slotB] = randomPair(prevPair);
      prevPair = `${slotA}-${slotB}`;
      const progress = shuffleSteps > 1 ? i / (shuffleSteps - 1) : 1;
      const edgeDistance = Math.abs(0.5 - progress) * 2; // 1 at start/end, 0 in middle
      const speedEnvelope = 1 + edgeDistance * 0.9; // slow -> fast -> slow
      const prepDuration = Math.round(36 * speedEnvelope);
      const crossDuration = Math.round(50 * speedEnvelope);
      const settleDuration = Math.round(28 * speedEnvelope);

      const cupA = bySlot(mapping, slotA);
      const cupB = bySlot(mapping, slotB);
      const cupC = [0, 1, 2].find((id) => id !== cupA && id !== cupB);

      const arc = 56 + Math.floor(Math.random() * 28);
      const crossRot = 7 + Math.floor(Math.random() * 8);

      await applyCupPatch(
        {
          [cupA]: { y: -(arc - 14), rot: -(crossRot + 1) },
          [cupB]: { y: -(arc - 14), rot: crossRot + 1 },
          [cupC]: { y: -10, rot: 0 },
        },
        prepDuration,
        "cubic-bezier(0.33, 1, 0.68, 1)"
      );

      await applyCupPatch(
        {
          [cupA]: { x: SLOT_X[slotB], y: -arc, rot: crossRot },
          [cupB]: { x: SLOT_X[slotA], y: -arc, rot: -crossRot },
          [cupC]: { y: 0, rot: 0 },
        },
        crossDuration,
        "cubic-bezier(0.65, 0, 0.35, 1)"
      );

      await applyCupPatch(
        {
          [cupA]: { y: 0, rot: 0 },
          [cupB]: { y: 0, rot: 0 },
        },
        settleDuration,
        "cubic-bezier(0.12, 0, 0.39, 0)"
      );

      mapping = {
        ...mapping,
        [cupA]: slotB,
        [cupB]: slotA,
      };
      setCupToSlot(mapping);
    }

    setStatusText("Выберите стаканчик");
    setPhase("choose");
    actionLockRef.current = false;
  };

  const onCupPick = async (cupId) => {
    if (phase !== "choose" || actionLockRef.current) return;
    actionLockRef.current = true;
    setPhase("reveal");
    setStatusText("Проверяем...");

    const selectedSlot = cupToSlot[cupId];

    let data;
    try {
      const response = await api.post("/magic-cup/play", {
        currency: "USDT",
        bet_amount_usd: Number(bet.toFixed(2)),
        selected_slot: selectedSlot,
      });
      data = response?.data || {};
    } catch (error) {
      haptic("light");
      setStatusText(getErrorMessage(error));
      setPhase("idle");
      actionLockRef.current = false;
      return;
    }

    const winningSlot = Number(data?.winning_slot);
    const winningCupId = Number.isInteger(winningSlot) ? bySlot(cupToSlot, winningSlot) : cupId;
    const win = Boolean(data?.win);
    const payout = Number(data?.payout_usd ?? 0);
    const net = Number((payout - bet).toFixed(2));

    setBallCupId(winningCupId);
    haptic(win ? "heavy" : "light");

    await sleep(70);
    await applyCupPatch(
      {
        0: { y: -96 },
        1: { y: -96 },
        2: { y: -96 },
      },
      210
    );

    setRevealedCupIds([0, 1, 2]);
    setLastOutcome({ win, payout, net });
    setStatusText(win ? `Выигрыш ${formatBet(payout)} USDT` : "Не угадали");
    setPhase("idle");
    actionLockRef.current = false;

    refreshData().catch(() => {});
  };

  const decBet = () => {
    if (controlsLocked) return;
    haptic("light");
    setSafeBet(bet - BET_STEP);
  };

  const incBet = () => {
    if (controlsLocked) return;
    haptic("light");
    setSafeBet(bet + BET_STEP);
  };

  const handleBetInputChange = (value) => {
    const normalized = value.replace(",", ".");
    if (!/^\d*\.?\d{0,2}$/.test(normalized)) return;
    setBetInput(normalized);
    if (!normalized) return;
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
      setBet(clampBet(parsed));
    }
  };

  const commitBetInput = () => {
    const parsed = Number(betInput.replace(",", "."));
    if (!Number.isFinite(parsed)) {
      setBetInput(formatBet(bet));
      return;
    }
    setSafeBet(parsed);
  };

  const quickSetBet = (value) => {
    if (controlsLocked) return;
    haptic("light");
    setSafeBet(value);
  };

  return (
    <div className="magic-cup-page animate-fade-in" data-testid="magic-cup-page">
      <section className="magic-cup-board">
        <div className={`magic-cup-stage ${phase === "mixing" ? "is-mixing" : ""}`}>
          <img src="/games/magic-cup/ball.svg" alt="" className="magic-ball" style={ballStyle} />

          {cups.map((cup) => (
            <button
              key={cup.id}
              type="button"
              className={`magic-cup-item ${phase === "choose" ? "can-pick" : ""}`}
              onClick={() => onCupPick(cup.id)}
              disabled={phase !== "choose"}
              style={{
                transform: `translate(-50%, -50%) translate(${cup.x}px, ${cup.y}px) rotate(${cup.rot}deg)`,
                transition: `transform ${transitionMs}ms ${transitionEase}`,
              }}
              data-testid={`magic-cup-${cup.id}`}
            >
              <img src="/games/magic-cup/cup.svg" alt="" className="magic-cup-image" draggable={false} />
            </button>
          ))}
        </div>

        <div className="magic-cup-payout-hint">Выплата: x{PAYOUT_MULTIPLIER.toFixed(2)}</div>
      </section>

      <section className="magic-cup-controls">
        {(statusText || lastOutcome) && <p className="magic-cup-status">{statusText}</p>}

        {lastOutcome && (
          <p className={`magic-cup-result ${lastOutcome.win ? "win" : "lose"}`}>
            {lastOutcome.win ? `+${formatBet(lastOutcome.net)} USDT чистая прибыль` : `-${formatBet(bet)} USDT`}
          </p>
        )}

        <div className="magic-cup-bet-head">
          <p className="magic-cup-bet-label">Ставка</p>
          <p className="magic-cup-available">Доступно: {formatBet(usdtAvailable)} USDT</p>
        </div>
        <div className="magic-cup-bet-row">
          <button type="button" onClick={decBet} className="magic-cup-bet-btn" disabled={controlsLocked}>−</button>
          <div className="magic-cup-bet-input-shell">
            <input
              className="magic-cup-bet-input"
              type="text"
              inputMode="decimal"
              value={betInput}
              onChange={(e) => handleBetInputChange(e.target.value)}
              onBlur={commitBetInput}
              disabled={controlsLocked}
              aria-label="Ставка в USDT"
            />
            <span className="magic-cup-bet-currency">USDT</span>
          </div>
          <button type="button" onClick={incBet} className="magic-cup-bet-btn" disabled={controlsLocked}>+</button>
        </div>

        <div className="magic-cup-quick-row">
          {[1, 2, 5, 10].map((value) => (
            <button
              key={value}
              type="button"
              className="magic-cup-quick-chip"
              onClick={() => quickSetBet(value)}
              disabled={controlsLocked}
            >
              {value} USDT
            </button>
          ))}
        </div>

        <button type="button" className="magic-cup-play-btn" onClick={startRound} disabled={controlsLocked}>
          {phase === "mixing" ? "Mixing..." : "Play"}
        </button>
      </section>
    </div>
  );
};

export default MagicCupPage;
